<template>
  <div class="relative flex flex-col">
    <ul
      ref="passengersList"
      class="invisible-scrollbar flex-grow divide-y overflow-auto"
    >
      <li class="px-4 pb-4 pt-2">
        <PassengerAmountSelect
          :value="adultCount"
          :min="1"
          :is-max="isMaxAdults || isMaxPassengers"
          :title="t('passengers.adult')"
          :description="t('passengers.adult-description')"
          @onMax="onAdultCountMax"
          @change="$emit('onAdultCountUpdate', $event)"
        />
      </li>
      <li
        v-for="[id, child] in childrenModel.children.value"
        :key="id"
        class="p-4"
      >
        <PassengerSelectedChild
          v-if="child.age !== undefined && !child.isEditing"
          :age="child.age"
          @onChildClick="onEditingStateToggle(id)"
          @onChildDelete="onChildDelete(id)"
        />
        <PassengerChildAgeGrid
          v-if="child.isEditing"
          :selected-age="child.age"
          @onHideGrid="onEditingStateToggle(id)"
          @onAgeSelect="onAgeSelect(id, $event)"
        />
      </li>
      <li v-if="isAddChildButtonVisible" class="p-4">
        <PassengerAddChildButton
          :disabled="isMaxChildren || isMaxPassengers"
          @click="onAddChild"
        />
      </li>
    </ul>
    <transition name="toast-transition">
      <div
        v-if="isErrorVisible"
        class="sticky inset-x-4 bottom-0 px-4 md:bottom-2"
      >
        <ErrorToast :text="errorMessage" />
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
  import { computed, nextTick, ref } from 'vue';

  // TODO: Move Toast to entities or UI, because it is base UI component
  import { ErrorToast, useErrorToast } from '@features/error-toast';
  import { PassengerAmountSelect } from '@features/passenger-amount-select';
  import {
    PassengerAddChildButton,
    PassengerChildAgeGrid,
    PassengerSelectedChild,
    usePassengerChildren,
  } from '@features/passenger-child-select';
  import type { PassengerChildrenMap } from '@entities/passengers-state';
  import { usePassengerCounterLocales } from '../locales';

  const props = defineProps<{
    adultCount: number;
    children: PassengerChildrenMap;
    isMaxAdults?: boolean;
    isMaxChildren?: boolean;
    isMaxPassengers?: boolean;
  }>();

  const emit = defineEmits<{
    onError: [errorText: string];
    onAdultCountUpdate: [newCount: number];
    onChildrenUpdate: [newState: PassengerChildrenMap];
    onAddChild: [];
  }>();

  const { t } = usePassengerCounterLocales();
  const { errorMessage, isErrorVisible, showError } = useErrorToast();
  const childrenModel = usePassengerChildren(props.children);

  const isAddChildButtonVisible = computed(() => {
    const childrenArray = Array.from(childrenModel.children.value.values());
    return !childrenArray.some(({ isEditing }) => isEditing);
  });

  const passengersList = ref<HTMLDivElement>();

  const showMaxPassengersError = () => {
    showError({
      text: t('passengers.validation.max-count'),
    });

    emit('onError', t('passengers.validation.max-count'));
  };

  const onAdultCountMax = () => {
    if (!props.isMaxPassengers) {
      return;
    }
    showMaxPassengersError();
  };

  const onEditingStateToggle = (childId: string) => {
    childrenModel.toggleEditingState(childId);
    emit('onChildrenUpdate', childrenModel.children.value);
  };

  const onAgeSelect = (childId: string, age: number) => {
    const isChangingExistingChild = childrenModel.children.value.has(childId);

    if (props.isMaxPassengers && !isChangingExistingChild) {
      showMaxPassengersError();
      return;
    }

    childrenModel.setChildAge(childId, age);
    emit('onChildrenUpdate', childrenModel.children.value);
  };

  const onChildDelete = (childId: string) => {
    childrenModel.deleteChild(childId);
    emit('onChildrenUpdate', childrenModel.children.value);
  };

  const onAddChild = () => {
    if (props.isMaxPassengers) {
      showMaxPassengersError();
      return;
    }

    if (props.isMaxChildren) {
      return;
    }

    childrenModel.addChild();
    emit('onAddChild');

    nextTick(() => {
      passengersList.value?.scrollTo({
        top: passengersList.value?.scrollHeight,
        behavior: 'smooth',
      });
    });
  };
</script>
